import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] border border-transparent text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:opacity-95",
        destructive:
          "border-destructive bg-destructive text-white hover:opacity-95 focus-visible:ring-destructive/30",
        dangerOutline:
          "border-destructive/30 bg-background text-destructive hover:bg-destructive/5 focus-visible:ring-destructive/30",
        outline:
          "border-border bg-background text-foreground hover:bg-muted",
        secondary:
          "border-border bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "border-transparent bg-transparent text-foreground hover:bg-muted",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-[8px] px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-[8px] px-3 text-sm has-[>svg]:px-2.5",
        lg: "h-10 rounded-[10px] px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-[10px]",
        "icon-xs": "size-6 rounded-[8px] [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 rounded-[8px]",
        "icon-lg": "size-10 rounded-[10px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  disabled = false,
  "aria-disabled": ariaDisabled,
  onClickCapture,
  onAuxClickCapture,
  tabIndex,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      disabled={asChild ? undefined : disabled}
      aria-disabled={asChild && disabled ? true : ariaDisabled}
      tabIndex={asChild && disabled ? -1 : tabIndex}
      onClickCapture={
        asChild && disabled
          ? (event) => {
              event.preventDefault()
              event.stopPropagation()
            }
          : onClickCapture
      }
      onAuxClickCapture={
        asChild && disabled
          ? (event) => {
              event.preventDefault()
              event.stopPropagation()
            }
          : onAuxClickCapture
      }
    />
  )
}

export { Button, buttonVariants }
